import { TranslocoService } from '@jsverse/transloco';
import { combineLatest, map, Observable } from 'rxjs';

import { DeviceUiChannelsDeviceBehaviorPlugin } from '@shure/device-ui/channels/data-access';
import { IntellimixType } from '@shure/device-ui/intellimix/data-access';
import { DeviceUiIntellimixDeviceBehaviorPlugin } from '@shure/device-ui/intellimix/data-access';
import {
	DeviceUiSettingsFactoryResetBehaviorPlugin,
	FactoryResetText
} from '@shure/device-ui/settings/factory-reset/data-access';
import { DeviceUiSettingsGeneralBehaviorPlugin } from '@shure/device-ui/settings/general/data-access';
import { LogicControlDeviceBehaviorPlugin } from '@shure/device-ui/settings/logic-control/data-access';
import { DspType } from '@shure/device-ui/shared/dsp/data-access';
import { DeviceUiPage, DeviceUiRoute } from '@shure/device-ui/shared/services';
import { DeviceUiShellDeviceBehaviorPlugin } from '@shure/device-ui/shell/data-access';
import { DeviceMixerType, DeviceModel } from '@shure/shared/angular/data-access/system-api/models';
import { DeviceBehaviorPlugin, DeviceNameBehaviorPlugin } from '@shure/shared/angular/device-behavior/core';
import { DeviceCoverageTemplateBehaviorPlugin } from '@shure/shared/angular/features/device-coverage/coverage-templates/feature';
import { DeviceCoverageDeviceBehaviorPlugin } from '@shure/shared/angular/features/device-coverage/data-access';
import { CoverageImage, CoverageImageProvider } from '@shure/shared/angular/features/device-coverage/ui';
import {
	LightsSettingsDeviceBehaviorPlugin,
	LightsSettingsSupportType
} from '@shure/shared/angular/features/device-lights-settings/data-access';
import { shUseTranslation } from '@shure/shared/angular/utils/i18n';

@shUseTranslation('scDevUiSharedDevBehavior', (lang) => import(`../i18n/${lang}`))
export class Mxa310DeviceUiDeviceBehaviorPlugin
	implements
		DeviceBehaviorPlugin,
		DeviceUiShellDeviceBehaviorPlugin,
		DeviceUiSettingsFactoryResetBehaviorPlugin,
		LightsSettingsDeviceBehaviorPlugin,
		DeviceUiIntellimixDeviceBehaviorPlugin,
		DeviceUiChannelsDeviceBehaviorPlugin,
		DeviceUiSettingsGeneralBehaviorPlugin,
		DeviceNameBehaviorPlugin,
		DeviceCoverageDeviceBehaviorPlugin,
		DeviceCoverageTemplateBehaviorPlugin,
		CoverageImageProvider,
		LogicControlDeviceBehaviorPlugin
{
	public readonly models: DeviceModel[] = [DeviceModel.MXA310];

	constructor(protected readonly transloco: TranslocoService) {}

	public hasTemplates(): boolean {
		return true;
	}

	public getCoverageImage(): CoverageImage {
		return {
			live: 'device-mxa310-back.svg',
			virtual: 'device-mxa310-back-line.svg',
			isImageRound: true
		};
	}
	public usePostGateValues(): boolean {
		return true;
	}

	public getAvailablePages$(
		_model: DeviceModel,
		_options?: { virtual?: boolean | undefined; autoCoverageMode?: boolean | undefined } | undefined
	): Observable<DeviceUiPage[] | undefined> {
		return combineLatest([
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.coverage'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.channels'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.automixer'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.presets'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.settings')
		]).pipe(
			map(([coverageLabel, channelsLabel, automixerLabel, presetsLabel, settingsLabel]) => [
				{
					route: DeviceUiRoute.Coverage,
					label: coverageLabel
				},
				{
					route: DeviceUiRoute.Channels,
					label: channelsLabel
				},
				{
					route: DeviceUiRoute.Intellimix,
					label: automixerLabel
				},
				{
					route: DeviceUiRoute.Presets,
					label: presetsLabel
				},
				{
					route: DeviceUiRoute.Settings,
					label: settingsLabel
				}
			])
		);
	}

	public getDefaultRoute(_model: DeviceModel): DeviceUiRoute | undefined {
		return DeviceUiRoute.Coverage;
	}

	public getResetText(_model: DeviceModel): FactoryResetText {
		return {
			tab$: this.transloco.selectTranslate('scDevUiSharedDevBehavior.settings.factory-reset'),
			title: this.transloco.translate('scDevUiSharedDevBehavior.settings.factory-reset'),
			button: this.transloco.translate(
				'scDevUiSharedDevBehavior.settings.factory-reset-page.restore-factory-defaults'
			)
		};
	}

	public getLightsSettingsSupportType(): LightsSettingsSupportType {
		return LightsSettingsSupportType.SupportedLegacy;
	}

	public isLightsNextSupported(): boolean {
		return false;
	}

	public getIntellimixType(_model: DeviceModel): IntellimixType | undefined {
		return IntellimixType.Advanced;
	}

	public canTurnOffAutomixer(_model: DeviceModel): boolean {
		return true;
	}

	public revertAutomixerDefaultsCapability(_model: DeviceModel): boolean {
		return false;
	}

	public getMixerType(_model: DeviceModel): DeviceMixerType | undefined {
		return DeviceMixerType.Automixer;
	}

	public showPatentInfo(): boolean {
		return true;
	}

	public getDeviceNameMaxLength(_model: DeviceModel): number | undefined {
		return 31;
	}

	public getChannelsChannelDspSortingMap(_model: DeviceModel): Map<DspType, number> | undefined {
		return new Map<DspType, number>([
			[DspType.Peq, 1],
			[DspType.Eq, 2]
		]);
	}
	public showPolarPatternAutomixWarningDialog(): boolean {
		return true;
	}

	public supportsChannels(): boolean {
		return true;
	}
}
